import React from 'react';
import cx from 'classnames';

import { RequestError } from 'api/utils';
import Slide from 'components/Animations/Slide';
import Dialog from 'components/Dialog';
import useWindowSize from 'hooks/useWindowSize';
import { noop } from 'utils';
import { BOX_SIZE } from './partials/ProductTagTooltip';
import styles from './TaggedImage.module.css';

export const CONTAINER_CLASSNAME = 'tagged-image-container';

const keepTagWithinHorizontalBounds = (element: HTMLElement | null, tag: Tag, boxSize: number) => {
  if (!element?.parentElement) return undefined;

  const parent = element.parentElement;
  const containerWidth = parent.getBoundingClientRect().width;
  const imageElementWidth = parent.querySelector('img')?.clientWidth ?? 0;

  // Calculate new X from tag X, which is a percentage
  const newX = (tag.x / 100) * containerWidth;

  const isRightOverflowing = newX + boxSize / 2 > containerWidth;
  const isLeftOverflowing = newX - boxSize / 2 < 0;

  let translateX = undefined;
  if (isRightOverflowing) {
    // Calculate how much of the box would overflow
    const outsideBox = newX + boxSize / 2 - containerWidth;

    // Calculate how many percent of the box is overflowing
    const percentage = (100 * outsideBox) / boxSize;

    // We also need to subtract the difference in width between the image container and the tag container
    translateX = `calc(-${50 + percentage}% - ${imageElementWidth - containerWidth}px)`;
  } else if (isLeftOverflowing) {
    // Calculate how much of the box would overflow
    const outsideBox = Math.abs(newX - boxSize / 2);

    // Calculate how many percent of the box is overflowing
    const percentage = (100 * outsideBox) / boxSize;

    translateX = `calc(-${50 - percentage}%)`;
  }

  return translateX;
};

export interface Tag {
  x: number;
  y: number;
}

export interface TaggedImageCallbacks<T, S> {
  getTagData: (tag: S) => Promise<T | undefined>;
  renderTagData: (
    data: T | undefined,
    onClose: () => void,
    open: boolean,
    translateX?: string,
  ) => React.ReactNode;
}

export interface TaggedTagProps<T, S> extends TaggedImageCallbacks<T, S> {
  tag: S;
}

export function TaggedTag<T, S extends Tag>({
  tag,
  getTagData,
  renderTagData,
}: TaggedTagProps<T, S>) {
  const { x, y } = tag;
  const [data, setData] = React.useState<T>();
  const [open, setOpen] = React.useState(false);

  const elementRef = React.useRef<HTMLDivElement | null>(null);

  const { breakpoints } = useWindowSize();
  const isTabletUp = breakpoints['tablet-portrait-up'];

  const getRelativePosition = (num: number) => `${num}%`;

  React.useEffect(() => {
    const interactionHandler = async () => {
      try {
        const responseData = await getTagData(tag);
        setData(responseData);
      } catch (error) {
        if (error instanceof RequestError) {
          console.error(error, error.response);
        }
      }
    };

    if (!data) {
      interactionHandler();
    }
  }, [getTagData, tag, data]);

  const DialogWrapper: React.FCWithChildren = ({ children }) => {
    if (!data) return null;

    return !isTabletUp ? (
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        noBackdrop={isTabletUp}
        noPortal={isTabletUp}
      >
        <Slide in={open}>{children}</Slide>
      </Dialog>
    ) : (
      <React.Fragment>{children}</React.Fragment>
    );
  };

  return (
    <div
      onMouseLeave={() => {
        if (isTabletUp) {
          setOpen(false);
        }
      }}
      style={{ top: getRelativePosition(y), left: getRelativePosition(x) }}
      className={styles.tag}
      ref={elementRef}
    >
      <button
        onMouseOver={() => {
          if (isTabletUp) {
            setOpen(true);
          }
        }}
        onClick={async () => {
          setOpen(!open);
        }}
      />
      <DialogWrapper>
        {renderTagData(
          data,
          !isTabletUp ? () => setOpen(false) : noop,
          open,
          keepTagWithinHorizontalBounds(elementRef?.current ?? null, tag, BOX_SIZE),
        )}
      </DialogWrapper>
    </div>
  );
}

export interface TaggedImageProps<T, S>
  extends TaggedImageCallbacks<T, S>,
    React.HTMLProps<HTMLImageElement> {
  tags?: S[];
}

function TaggedImage<T, S extends Tag>({
  src,
  srcSet,
  alt,
  tags = [],
  getTagData,
  renderTagData,
}: TaggedImageProps<T, S>) {
  return (
    <div className={cx(styles.root, CONTAINER_CLASSNAME)}>
      <img className={styles.image} src={src} srcSet={srcSet} alt={alt} />

      {tags.map((tag, index) => (
        <TaggedTag<T, S>
          renderTagData={renderTagData}
          getTagData={getTagData}
          key={index}
          tag={tag}
        />
      ))}
    </div>
  );
}

export default TaggedImage;
